import type { ImplementationActionStep } from '@whizz/api-model';
import React, { useState } from 'react';
import { useI18n } from '@whizz/react-i18n';
import { cohortLabel, isActionGoalKey, splitFirstSentence } from './actionSteps';
import type { ActionGoal, CohortOptions } from './actionSteps';
import CollapseSection from '../../pages/Home/modules/CollapseSection';
import { CollapsibleCard } from './CollapsibleCard';

interface Props {
  goals: ActionGoal[];
  genericGuidance?: ImplementationActionStep[];
  cohorts: CohortOptions;
}

const GENERAL_KEY = 'general';

const StepText = ({ text }: { text: string }): JSX.Element => {
  const sentence = splitFirstSentence(text);
  return sentence ? <><strong className={'font-semibold'}>{sentence.first}</strong> {sentence.rest}</> : <>{text}</>;
};

const GuidanceItem = ({ title, badge, open, onToggle, steps, emptyText }: {
  title: string; badge?: string; open: boolean; onToggle: () => void; steps: ImplementationActionStep[]; emptyText: string;
}): JSX.Element => (
  <CollapsibleCard title={title} badge={badge} open={open} onToggle={onToggle}>
    {steps.length ? (
      <ul className={'list-disc pl-8 pr-4 pb-4 space-y-2 text-sm leading-relaxed'}>
        {steps.map((step) => <li key={step.id}><StepText text={step.text} /></li>)}
      </ul>
    ) : (
      <div className={'mx-4 mb-4 p-3 border border-dashed border-gray-300 rounded-md text-xs text-center text-gray-400'}>{emptyText}</div>
    )}
  </CollapsibleCard>
);

export const ImplementationActionSteps = ({ goals, cohorts, genericGuidance = [] }: Props): JSX.Element => {
  const { translate: t } = useI18n();
  const [ open, setOpen ] = useState(true);
  const [ expanded, setExpanded ] = useState<Record<string, boolean>>({});
  const prefix = 'pages.IMPLEMENTATION_ACTION_STEPS';
  const labels = { school: t(`${prefix}.SCHOOL`), unresolved: t(`${prefix}.UNRESOLVED_COHORT`) };
  const goalKey = (goal: ActionGoal, index: number) => goal.id ?? `${goal.goal_key}-${index}`;
  const isExpanded = (key: string, defaultOpen: boolean) => expanded[key] ?? defaultOpen;
  const toggle = (key: string, defaultOpen: boolean) => () => setExpanded((current) => ({ ...current, [key]: !(current[key] ?? defaultOpen) }));

  return (
    <CollapseSection autoScroll={false} headline={t(`${prefix}.TITLE`)} dataCy={'implementation_guidance'} open={open} setOpen={() => setOpen((value) => !value)}>
      <div className={'flex flex-col gap-3'}>
        <p className={'text-sm'}>{t(`${prefix}.INTRO`)}</p>
        {!!genericGuidance.length && (
          <GuidanceItem
            title={t(`${prefix}.GENERAL_TITLE`)}
            open={isExpanded(GENERAL_KEY, true)}
            onToggle={toggle(GENERAL_KEY, true)}
            steps={genericGuidance}
            emptyText={t(`${prefix}.UNAVAILABLE`)}
          />
        )}
        {!goals.length && <p className={'text-sm text-gray-500'}>{t(`${prefix}.EMPTY`)}</p>}
        {goals.map((goal, index) => {
          const key = goalKey(goal, index);
          return (
            <GuidanceItem
              key={key}
              title={isActionGoalKey(goal.goal_key) ? t(`pages.ADMIN.IMPACT_REPORTS.GOALS.${goal.goal_key}`) : t(`${prefix}.UNKNOWN_GOAL`)}
              badge={cohortLabel(goal, cohorts, labels)}
              open={isExpanded(key, false)}
              onToggle={toggle(key, false)}
              steps={goal.actionSteps ?? []}
              emptyText={t(`${prefix}.UNAVAILABLE`)}
            />
          );
        })}
      </div>
    </CollapseSection>
  );
};
